import { getV1CompaniesCompanyIdComplianceCheck } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import connection from '~services/connectionModule';

import { ComplianceCheck, ComplianceIssue } from './models';

export interface ComplianceCheckResult {
  hasIssues: boolean;
  warnings: ComplianceIssue[];
  blocking: ComplianceIssue[];
  error?: boolean;
}

export default function useComplianceCheck() {
  const inFlightCount = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ComplianceCheck | null>(null);

  const check = useCallback(
    async (
      companyId: string,
      accountId?: string,
      userId?: string,
      equipmentId?: string,
    ) => {
      inFlightCount.current += 1;
      setIsLoading(true);
      try {
        const response = await getV1CompaniesCompanyIdComplianceCheck({
          path: { 'company-id': companyId },
          query: { account_id: accountId, user_id: userId, equipment_id: equipmentId },
        });
        const parsed = ComplianceCheck.parse(response.data.data);
        setResult(parsed);
        return parsed;
      } catch (error) {
        const fallback = new ComplianceCheck(true, [], []);
        setResult(fallback);
        try {
          connection.handleRequestError(
            error,
            t('error_messages.compliance_document_types.failed_to_check'),
          );
        } catch {
          // Error already handled (toast + Sentry)
        }
        return fallback;
      } finally {
        inFlightCount.current -= 1;
        if (inFlightCount.current === 0) {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setResult(null);
  }, []);

  return { check, isLoading, reset, result };
}
